// FILE: components/reviews/ReviewDetail.tsx
"use client";

function relativeTime(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)    return "Vừa xong";
  if (diff < 3600)  return `${Math.floor(diff / 60)} phút trước`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} giờ trước`;
  const days = Math.floor(diff / 86400);
  if (days === 1)   return "Hôm qua";
  if (days < 7)     return `${days} ngày trước`;
  return new Date(dateStr).toLocaleDateString("vi-VN");
}
import { submitComment, getComments } from "@/actions/comments";
import type { Comment } from "@/actions/comments";
import { useState, useRef, useCallback, useEffect } from "react";
import { ThumbsUp, Send, Smile, X } from "lucide-react";
import { MentionsInput, Mention } from "react-mentions";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import UserLink from "@/components/shared/UserLink";
import { createClient } from "@/utils/supabase/client";
import type { ReviewData } from "./types";

// ── Types ─────────────────────────────────────────────────────
interface MentionUser { id: string; display: string; avatar?: string; }

interface LocalReply {
  id: string; author: string; authorId: string;
  avatar: string; avatarColor: string;
  text: string; time: string; likes: number;
}

// ── Mention input styles ──────────────────────────────────────
const mentionStyle = {
  control: { fontSize: 13, lineHeight: "1.6" },
  input:   { outline: "none", border: "none", background: "transparent", padding: 0, minHeight: "1.4rem" },
  suggestions: {
    list: {
      background: "white", border: "1px solid #e2e8f0",
      borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,.12)",
      overflow: "hidden", minWidth: 220,
    },
    item: { padding: "8px 12px", cursor: "pointer", fontSize: 12 },
  },
};

const mentionMarkup = "@[__display__](__id__)";

// ── Parse mentions ────────────────────────────────────────────
function parseMentions(content: string): { plainText: string; mentionedIds: string[] } {
  const mentionedIds: string[] = [];
  const plainText = content.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, (_, display, id) => {
    mentionedIds.push(id);
    return `@${display}`;
  });
  return { plainText, mentionedIds };
}

// ── Render content với mention highlight ─────────────────────
function RenderContent({ text }: { text: string }) {
  const parts = text.split(/(@\[[^\]]+\]\([^)]+\))/g);
  return (
    <>
      {parts.map((part, i) => {
        const match = part.match(/@\[([^\]]+)\]\(([^)]+)\)/);
        if (match) return <span key={i} className="text-blue-500 font-medium">@{match[1]}</span>;
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}

function LikeButton({ likes }: { likes: number }) {
  const [liked, setLiked] = useState(false);
  return (
    <button
      onClick={() => setLiked(p => !p)}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium transition-all ${
        liked ? "text-orange-500 bg-orange-50" : "text-muted-foreground hover:text-foreground hover:bg-muted"
      }`}>
      <ThumbsUp className={`w-3 h-3 ${liked ? "fill-orange-500" : ""}`} />
      {likes + (liked ? 1 : 0)}
    </button>
  );
}

// ── Reply Input ───────────────────────────────────────────────
function ReplyInput({
  placeholder = "Viết phản hồi...",
  parentId, onSubmit, onCancel, autoFocus = false,
}: {
  placeholder?: string; parentId: string | null;
  onSubmit: (content: string, parentId: string | null, mentionedIds: string[]) => void;
  onCancel?: () => void; autoFocus?: boolean;
}) {
  const [value,     setValue]     = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const inputRef    = useRef<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) setTimeout(() => inputRef.current?.focus?.(), 50);
  }, [autoFocus]);

  const fetchUsers = useCallback((query: string, callback: (u: MentionUser[]) => void) => {
    if (!query.trim()) return callback([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const supabase = createClient();
      const { data } = await supabase
        .from("users")
        .select("id, full_name, username, avatar_url")
        .or(`full_name.ilike.%${query}%,username.ilike.%${query}%`)
        .limit(6);
      callback((data ?? []).map(u => ({
        id:      u.id,
        display: u.full_name || u.username || "User",
        avatar:  u.avatar_url ?? undefined,
      })));
    }, 300);
  }, []);

  const handleEmojiClick = useCallback((emojiData: EmojiClickData) => {
  const textarea = containerRef.current?.querySelector("textarea") as HTMLTextAreaElement | null;
  if (textarea) {
    const start  = textarea.selectionStart ?? value.length;
    const end    = textarea.selectionEnd   ?? value.length;
    const newVal = value.slice(0, start) + emojiData.emoji + value.slice(end);
    setValue(newVal);
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + emojiData.emoji.length;
      textarea.setSelectionRange(pos, pos);
    });
  } else {
    setValue(prev => prev + emojiData.emoji);
  }
  setShowEmoji(false);
}, [value]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
    if (e.key === "Escape") { setValue(""); onCancel?.(); }
  }

  function handleSubmit() {
    if (!value.trim()) return;
    const { mentionedIds } = parseMentions(value);
    onSubmit(value, parentId, mentionedIds);
    setValue("");
    setShowEmoji(false);
  }

  return (
    <div className="flex items-end gap-2">
      <div className={`flex-1 flex items-end gap-1.5 bg-[#f1f5f9] rounded-2xl px-3.5 py-2 transition-all ${value ? "ring-2 ring-teal-500/20" : ""}`}>
        <div ref={containerRef} className="flex-1 min-w-0">
  <MentionsInput
    inputRef={inputRef}
    value={value}
    onChange={(_, newVal) => setValue(newVal)}
    onKeyDown={handleKeyDown}
    placeholder={placeholder}
    style={mentionStyle}
    className="flex-1 min-w-0"
    allowSuggestionsAboveCursor
  >
    <Mention
      trigger="@"
      data={fetchUsers}
      markup={mentionMarkup}
      displayTransform={(_, display) => `@${display}`}
      renderSuggestion={(entry: any) => (
        <div className="flex items-center gap-2.5 px-3 py-2 hover:bg-[#f8fafc]">
          {entry.avatar ? (
            <img src={entry.avatar} alt={entry.display} className="w-7 h-7 rounded-full object-cover shrink-0" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center shrink-0">
              <span className="text-teal-700 text-[11px] font-bold">{entry.display.slice(0,1).toUpperCase()}</span>
            </div>
          )}
          <span className="text-[12px] font-medium text-[#0f172a]">{entry.display}</span>
        </div>
      )}
      style={{ backgroundColor: "#e0f2fe", borderRadius: 4, color: "#0284c7", padding: "0 2px" }}
    />
  </MentionsInput>
</div>

        <div className="flex items-center gap-0.5 shrink-0 pb-0.5">
          <div className="relative">
            <button type="button" onClick={() => setShowEmoji(p => !p)}
              className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${showEmoji ? "text-amber-500 bg-amber-50" : "text-[#94a3b8] hover:text-[#64748b] hover:bg-white"}`}>
              <Smile className="w-4 h-4" />
            </button>
            {showEmoji && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowEmoji(false)} />
                <div className="absolute bottom-full right-0 mb-2 z-20">
                  <EmojiPicker onEmojiClick={handleEmojiClick} height={320} width={280}
                    searchPlaceholder="Tìm emoji..." previewConfig={{ showPreview: false }} />
                </div>
              </>
            )}
          </div>
          <button onClick={handleSubmit} disabled={!value.trim()}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
              value.trim() ? "bg-teal-800 text-white hover:bg-teal-700 shadow-[0_1px_4px_rgba(15,58,74,.2)]" : "text-[#cbd5e1] cursor-not-allowed"
            }`}>
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {onCancel && (
        <button onClick={onCancel}
          className="w-7 h-7 rounded-full flex items-center justify-center text-[#94a3b8] hover:bg-[#f1f5f9] hover:text-red-400 transition-colors shrink-0 mb-0.5">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────
interface ReviewDetailProps {
  review: ReviewData;
  onNavigate?: (page: string) => void;
}

export default function ReviewDetail({ review, onNavigate }: ReviewDetailProps) {
  const [localReplies, setLocalReplies] = useState<LocalReply[]>([]);
  const [replyingTo,   setReplyingTo]   = useState<string | null>(null);
  const [dbComments,   setDbComments]   = useState<Comment[]>([]);
const [currentUser, setCurrentUser] = useState<{ name: string; avatar: string | null } | null>(null);

useEffect(() => {
  const supabase = createClient();
  supabase.auth.getUser().then(async ({ data: { user } }) => {
    if (!user) return;
    const { data } = await supabase.from("users").select("full_name, username, avatar_url").eq("id", user.id).single();
    setCurrentUser({
      name:   data?.full_name
           ?? data?.username
           ?? user.user_metadata?.full_name
           ?? user.user_metadata?.name
           ?? "U",
      avatar: data?.avatar_url
           ?? user.user_metadata?.avatar_url
           ?? user.user_metadata?.picture
           ?? null,
    });
  });
}, []);

  useEffect(() => {
    getComments("review", review.id).then(setDbComments);
  }, [review.id]);

  const mapped = [...dbComments.map(c => {
  const auth = Array.isArray(c.author) ? c.author[0] : c.author;
  return {
    id:        c.id,
    author:    auth?.full_name ?? auth?.username ?? "User",
    authorId:  auth?.id ?? "",
    avatar:    auth?.avatar_url ?? (auth?.full_name ?? "U").slice(0,1).toUpperCase(),
    avatarColor: "#0f3a4a",
    text:      c.content,
    time:      relativeTime(c.created_at),
    likes:     0,
    parent_id: c.parent_id ?? null,
  };
}), ...localReplies];

const rootReplies  = mapped.filter(r => !(r as any).parent_id);
const childReplies = mapped.filter(r => !!(r as any).parent_id);

  async function handleSubmit(content: string, parentId: string | null, mentionedIds: string[]) {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  const { data: profile } = user
    ? await supabase.from("users").select("id, full_name, username, avatar_url").eq("id", user.id).single()
    : { data: null };

  const optimisticId = `local-${Date.now()}`;
  const optimistic: LocalReply = {
    id:          optimisticId,
    author:      profile?.full_name ?? profile?.username ?? "Bạn",
    authorId:    profile?.id ?? "me",
    avatar:      profile?.avatar_url ?? (profile?.full_name ?? "B").slice(0,1).toUpperCase(),
    avatarColor: "#0f3a4a",
    text:        content,
    time:        "Vừa xong",
    likes:       0,
  };

  setLocalReplies(prev => [...prev, optimistic]);
  setReplyingTo(null);

  const res = await submitComment({
    content,
    parent_id:           parentId,
    mentioned_user_ids:  mentionedIds,
    target_type:         "review",
    target_id:           review.id,
  });

  if (res.success) {
    setLocalReplies(prev => prev.filter(r => r.id !== optimisticId));
    getComments("review", review.id).then(setDbComments);
  }
}

  return (
    <div className="py-3 space-y-1">
      {/* ── Replies ── */}
      {rootReplies.length > 0 ? (
        <div className="ml-0 sm:ml-[52px] pl-3 sm:pl-4 border-l-2 border-border/70 flex flex-col gap-0 mb-3">
          {rootReplies.map(reply => (
            <div key={reply.id} className="py-3 first:pt-1">
              <div className="flex gap-2.5">
                {typeof (reply as any).avatar === "string" && (reply as any).avatar.startsWith("http") ? (
  <img src={(reply as any).avatar} alt={reply.author}
    className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" />
) : (
  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[9px] font-extrabold shrink-0 mt-0.5"
    style={{ background: (reply as any).avatarColor ?? "#0f3a4a" }}>
    {(reply as any).avatar}
  </div>
)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <UserLink name={reply.author} userId={reply.authorId} onNavigate={onNavigate} className="text-[12px]" />
                    <span className="text-[10px] text-muted-foreground">{reply.time}</span>
                  </div>
                  <div className="bg-white border border-border rounded-[4px_14px_14px_14px] px-3.5 py-2.5 text-[13px] text-foreground leading-[1.65] shadow-[0_1px_2px_rgba(0,0,0,.03)]">
                    <RenderContent text={reply.text} />
                  </div>
                  <div className="flex items-center gap-1 mt-1.5">
                    <LikeButton likes={reply.likes} />
                    <button
                      onClick={() => setReplyingTo(prev => prev === reply.id ? null : reply.id)}
                      className="text-[11px] font-medium text-teal-600 hover:text-teal-800 px-2 py-1 rounded-full hover:bg-teal-50 transition-all">
                      Phản hồi
                    </button>
                  </div>
                  {replyingTo === reply.id && (
  <div className="mt-2 ml-4 sm:ml-10 pl-2 sm:pl-3 border-l-2 border-[#e2e8f0]">
    <p className="text-[10px] text-[#94a3b8] mb-1.5">
      Đang phản hồi <strong className="text-teal-700">@{reply.author}</strong>
    </p>
    <ReplyInput
      placeholder={`Phản hồi @${reply.author}...`}
      parentId={reply.id}
      onSubmit={handleSubmit}
      onCancel={() => setReplyingTo(null)}
      autoFocus
    />
  </div>
)}
{childReplies.filter(r => (r as any).parent_id === reply.id).length > 0 && (
  <div className="ml-9 mt-2 pl-3 border-l-2 border-[#e2e8f0] space-y-2">
    {childReplies.filter(r => (r as any).parent_id === reply.id).map(child => (
      <div key={child.id} className="flex gap-2">
        {typeof child.avatar === "string" && child.avatar.startsWith("http") ? (
          <img src={child.avatar} alt={child.author} className="w-6 h-6 rounded-full object-cover shrink-0 mt-0.5" />
        ) : (
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-extrabold shrink-0 mt-0.5"
            style={{ background: (child as any).avatarColor }}>
            {child.avatar}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-[12px] font-semibold text-[#0f172a]">{child.author}</span>
            <span className="text-[10px] text-muted-foreground">{child.time}</span>
          </div>
          <div className="bg-white border border-border rounded-[4px_12px_12px_12px] px-3 py-2 text-[12px] text-foreground leading-relaxed shadow-[0_1px_2px_rgba(0,0,0,.03)]">
            <RenderContent text={child.text} />
          </div>
        </div>
      </div>
    ))}
  </div>
)}
                </div>
              </div>
            </div>
          ))}

          {/* Inline reply input */}
          
        </div>
      ) : (
        <div className="py-3 mb-2">
          <p className="text-[12px] text-muted-foreground/60 italic">Chưa có phản hồi nào. Hãy là người đầu tiên!</p>
        </div>
      )}

      {/* ── Main input ── */}
      <div className="flex gap-2.5 items-start">
        {currentUser?.avatar ? (
  <img src={currentUser.avatar} alt={currentUser.name}
    className="w-7 h-7 rounded-full object-cover shrink-0 mt-1" />
) : (
  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-600 to-teal-400 flex items-center justify-center text-white text-[9px] font-extrabold shrink-0 mt-1">
    {(currentUser?.name ?? "U").slice(0,1).toUpperCase()}
  </div>
)}
        <div className="flex-1 min-w-0">
          <ReplyInput
            placeholder="Viết phản hồi... (Enter gửi, Shift+Enter xuống dòng)"
            parentId={null}
            onSubmit={handleSubmit}
          />
          <p className="text-[10px] text-[#94a3b8] mt-1 ml-1">Gõ @ để nhắc tên ai đó</p>
        </div>
      </div>
    </div>
  );
}
